import { useMemo } from 'react'
import {
  type AccountRollbackInput,
  type AccountRollbackPreview,
  type AccountRollbackSuccess,
  isDefined,
  Logger,
} from '@terros/common'
import { useTantalimQuery } from '../query'
import { useTantalimMutation } from '../mutation'
import { accountRollbackPreviewKey } from '../keys'
import { mutationUpdate, type GetHookResponse, type UpdateHookResponse } from '../hooks'

const logger = new Logger('useAccountRollback')

const IDLE_ROLLBACK_INPUT: AccountRollbackInput = {
  filter: { workflowId: 'WF.idle' },
  dryRun: true,
}

export type AccountRollbackRunInput = Omit<AccountRollbackInput, 'dryRun'>

export function useAccountRollback(): UpdateHookResponse<AccountRollbackRunInput, AccountRollbackSuccess> {
  logger.verbose('calling useAccountRollback')
  const mutation = useTantalimMutation<AccountRollbackInput, AccountRollbackSuccess>({ url: '/account/rollback' })

  async function update(input: AccountRollbackRunInput): Promise<AccountRollbackSuccess> {
    const result = await mutation.mutateAsync({ ...input, dryRun: false })
    logger.verbose('account rollback request sent')
    return result
  }

  return mutationUpdate(mutation, update)
}

export function useAccountRollbackPreview(
  input: AccountRollbackRunInput | undefined
): GetHookResponse<AccountRollbackPreview | undefined> {
  const request = useMemo((): AccountRollbackInput => {
    if (isDefined(input)) return { ...input, dryRun: true }
    return IDLE_ROLLBACK_INPUT
  }, [input])

  return useTantalimQuery<AccountRollbackInput, AccountRollbackSuccess, AccountRollbackPreview | undefined>({
    url: '/account/rollback',
    queryKey: accountRollbackPreviewKey(request),
    input: request,
    convertResponse: (data) => data.preview,
    options: {
      enabled: isDefined(input),
      staleTime: 0,
      refetchOnWindowFocus: false,
    },
  })
}
